import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { queryClient } from '~/app/query-client'
import { authApi } from '~/modules/auth/api'
import type { AccountContacts, AuthTokens, SessionPayload, Workspace } from '~/modules/auth/types'

/**
 * The session.
 *
 * Two rules this store exists to enforce:
 *
 * 1. **No token ever reaches web storage.** The access token lives in this ref, in memory, and
 *    dies with the tab. The refresh token lives in an `HttpOnly` cookie the JavaScript cannot
 *    read. Put either in `localStorage` and a single XSS becomes a permanent session handover.
 * 2. **The server decides what the user may do.** `roles`, `workspaces` and `capabilities` come
 *    from `GET /api/v2/auth/session/` and are never computed here. `can()` and `hasWorkspace()`
 *    are the only permission accessors in the app, and they are UX — every one is enforced
 *    again server-side.
 */
export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(null)
  const user = ref<SessionPayload['user'] | null>(null)
  const roles = ref<string[]>([])
  const workspaces = ref<Workspace[]>([])
  const defaultWorkspace = ref<Workspace | null>(null)
  const capabilities = ref<string[]>([])
  const featureFlags = ref<Record<string, boolean>>({})
  const requiresPasswordSetup = ref(false)
  /** The WhatsApp number and the email, each confirmed or not, and `must_confirm` (2026-10-27). */
  const contacts = ref<AccountContacts | null>(null)
  const affiliate = ref<SessionPayload['affiliate']>(null)

  /** The workspace whose sidebar and route tree are showing. */
  const activeWorkspace = ref<Workspace | null>(null)
  /** Set when the user chooses "skip for now" on password setup. Session-scoped on purpose:
   *  they are re-prompted next sign-in rather than nagged on every navigation. */
  const passwordSetupSkipped = ref(false)

  /** Has `bootstrap()` finished at least once? The guard uses it to run exactly once. */
  const bootstrapped = ref(false)
  let inFlight: Promise<void> | null = null

  const isAuthenticated = computed(() => user.value !== null)
  const hasDashboard = computed(() => workspaces.value.length > 0)

  function can(capability: string): boolean {
    return capabilities.value.includes(capability)
  }

  function hasWorkspace(workspace: Workspace): boolean {
    return workspaces.value.includes(workspace)
  }

  function isFlagEnabled(key: string): boolean {
    return featureFlags.value[key] === true
  }

  function applySession(session: SessionPayload) {
    user.value = session.user
    roles.value = session.roles
    workspaces.value = session.workspaces
    defaultWorkspace.value = session.default_workspace
    capabilities.value = session.capabilities
    featureFlags.value = session.feature_flags
    requiresPasswordSetup.value = session.requires_password_setup
    contacts.value = session.contacts ?? null
    affiliate.value = session.affiliate
    if (!activeWorkspace.value || !session.workspaces.includes(activeWorkspace.value)) {
      activeWorkspace.value = session.default_workspace
    }
  }

  /** When the current access token stops being usable. Drives the proactive refresh. */
  const accessExpiresAt = ref<number | null>(null)
  let refreshTimer: ReturnType<typeof setTimeout> | null = null

  function setTokens(tokens: AuthTokens) {
    accessToken.value = tokens.access
    const expiry = Date.parse(tokens.access_expires_at)
    accessExpiresAt.value = Number.isNaN(expiry) ? null : expiry
    scheduleRefresh()
  }

  /**
   * Refresh at ~80% of the access token's life, while the tab is visible.
   *
   * The interceptor's reactive refresh is the safety net; this is so an active user never pays
   * for the 401 round trip in the middle of doing something. Only while visible: a background
   * tab left open overnight should not sit there rotating tokens, and it gets a fresh one from
   * the focus handler below the moment anyone looks at it again.
   */
  function scheduleRefresh() {
    if (refreshTimer) clearTimeout(refreshTimer)
    refreshTimer = null
    if (typeof window === 'undefined' || accessExpiresAt.value === null) return

    const lifetime = accessExpiresAt.value - Date.now()
    if (lifetime <= 0) return
    // Never sooner than 30s, so a very short-lived token cannot spin.
    const delay = Math.max(lifetime * 0.8, 30_000)
    refreshTimer = setTimeout(() => {
      if (document.visibilityState === 'visible' && isAuthenticated.value) void refresh()
    }, delay)
  }

  /**
   * Re-check the session when the tab comes back after a long idle.
   *
   * A user who leaves a dashboard open over lunch returns to stale roles and, quite possibly,
   * an expired token. Re-bootstrapping picks up both. The threshold keeps ordinary tab
   * switching from firing a request every few seconds.
   */
  const IDLE_REBOOTSTRAP_MS = 5 * 60 * 1000
  let hiddenAt: number | null = null

  function watchVisibility() {
    if (typeof document === 'undefined') return () => {}
    const onChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now()
        return
      }
      const away = hiddenAt === null ? 0 : Date.now() - hiddenAt
      hiddenAt = null
      if (isAuthenticated.value && away >= IDLE_REBOOTSTRAP_MS) void bootstrap(true)
      scheduleRefresh()
    }
    document.addEventListener('visibilitychange', onChange)
    return () => document.removeEventListener('visibilitychange', onChange)
  }

  /**
   * Load the session once.
   *
   * Concurrent callers share one request — the router guard and a mounting component both want
   * it, and two bootstraps would race to populate the store.
   */
  async function bootstrap(force = false): Promise<void> {
    if (bootstrapped.value && !force) return
    if (inFlight) return inFlight

    inFlight = (async () => {
      try {
        applySession(await authApi.session())
      } catch {
        // No session, or an expired one the interceptor could not refresh. Not an error the
        // user needs to see: the guard will send them to login.
        reset()
      } finally {
        bootstrapped.value = true
        inFlight = null
      }
    })()
    return inFlight
  }

  /**
   * Exchange a refresh token for a new pair.
   *
   * Called by the axios interceptor, which serialises concurrent 401s into one call. The
   * refresh token is in the `HttpOnly` cookie, so there is nothing to pass.
   */
  async function refresh(): Promise<boolean> {
    try {
      setTokens(await authApi.refresh())
      return true
    } catch {
      return false
    }
  }

  function reset() {
    if (refreshTimer) clearTimeout(refreshTimer)
    refreshTimer = null
    accessExpiresAt.value = null
    accessToken.value = null
    user.value = null
    roles.value = []
    workspaces.value = []
    defaultWorkspace.value = null
    capabilities.value = []
    featureFlags.value = {}
    requiresPasswordSetup.value = false
    contacts.value = null
    affiliate.value = null
    activeWorkspace.value = null
    passwordSetupSkipped.value = false
  }

  /**
   * Sign out.
   *
   * The query cache is cleared, not merely invalidated: signing in as someone else on the same
   * tab must never render the previous user's cached property list, even for one frame.
   */
  async function logout() {
    try {
      await authApi.logout()
    } catch {
      // A failed logout call must still clear the client. The refresh token's cookie is
      // scoped to the auth path and expires; leaving a populated store behind is worse.
    }
    reset()
    bootstrapped.value = true
    queryClient.clear()
  }

  function switchWorkspace(workspace: Workspace) {
    if (hasWorkspace(workspace)) activeWorkspace.value = workspace
  }

  return {
    accessToken,
    accessExpiresAt,
    user,
    roles,
    workspaces,
    defaultWorkspace,
    capabilities,
    featureFlags,
    requiresPasswordSetup,
    contacts,
    affiliate,
    activeWorkspace,
    passwordSetupSkipped,
    bootstrapped,
    isAuthenticated,
    hasDashboard,
    can,
    hasWorkspace,
    isFlagEnabled,
    applySession,
    setTokens,
    bootstrap,
    refresh,
    reset,
    logout,
    switchWorkspace,
    scheduleRefresh,
    watchVisibility,
  }
})
